import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { centsToSubtotal, entriesForSubtotal, fetchShopEntriesRate, withCommas } from '@theme/clean-sweeps';
import { syncCartEntryProperties, syncCartTotalAttribute } from '@theme/checkout-entries';

/**
 * Cart entries total + per-line labels.
 * Fixed lines (membership / reward / metafield) keep their count.
 * Paid merch uses the shop Clean Sweeps rate — same API as
 * clean-sweeps-promo-text with Content source = Clean Sweeps rate.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} count - The numeric total.
 *
 * @extends {Component<Refs>}
 */
class CartTotalEntries extends Component {
  requiredRefs = ['count'];

  /** @type {Promise<{ perDollar: number }>|null} */
  #ratePromise = null;

  /** @type {number} */
  #calcId = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.#recalculate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#recalculate();
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then((result) => {
        if (result?.detail?.source === 'checkout-entries') return;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.#recalculate());
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-total-entries] Event promise rejected:', error);
      });
  };

  /**
   * @returns {HTMLElement}
   */
  #cartRoot() {
    return this.closest('cart-items-component') ?? document.body;
  }

  /**
   * @returns {number}
   */
  #multiplierFactor() {
    return Number(this.dataset.multiplierFactor) || 3;
  }

  /**
   * @returns {HTMLElement[]}
   */
  #rows() {
    return Array.from(this.#cartRoot().querySelectorAll('[data-cart-entries-line]')).filter(
      (el) => el instanceof HTMLElement
    );
  }

  /**
   * @param {HTMLElement} row
   * @returns {boolean}
   */
  #isMerchRow(row) {
    return row.dataset.entriesMultiplier !== 'true' && !(Number(row.dataset.fixedEntries) > 0);
  }

  /**
   * @returns {Promise<{ perDollar: number }>}
   */
  #shopRate() {
    if (!this.#ratePromise) {
      const shopGid = this.dataset.shopGid || document.body?.dataset?.shopGid || '';
      const sweepstakeId = this.dataset.sweepstakeId || document.body?.dataset?.sweepstakeId || '';
      this.#ratePromise = fetchShopEntriesRate(shopGid, sweepstakeId).then((rate) => {
        if (!(rate.perDollar > 0)) this.#ratePromise = null;
        return rate;
      });
    }
    return this.#ratePromise;
  }

  /**
   * @param {HTMLElement} row
   * @param {number} perDollar
   * @returns {number}
   */
  #unitEntriesFor(row, perDollar) {
    const fixed = Number(row.dataset.fixedEntries);
    if (Number.isFinite(fixed) && fixed > 0) return fixed;

    return entriesForSubtotal(centsToSubtotal(row.dataset.priceCents), perDollar);
  }

  #recalculate() {
    void this.#recalculateAsync();
  }

  async #recalculateAsync() {
    const calcId = ++this.#calcId;
    const rows = this.#rows();
    const hasMerch = rows.some((row) => this.#isMerchRow(row));

    let perDollar = 0;
    if (hasMerch) {
      const rate = await this.#shopRate();
      if (calcId !== this.#calcId) return;
      perDollar = rate.perDollar;
      if (!(perDollar > 0)) {
        this.#paintPending(rows);
        return;
      }
    } else if (calcId !== this.#calcId) {
      return;
    }

    const multiplierFactor = this.#multiplierFactor();

    if (rows.length === 0) {
      this.#paintTotal(0);
      this.#paintMultiplierPreview(0);
      void syncCartTotalAttribute(0);
      return;
    }

    let hasCartMultiplier = false;
    let baseTotal = 0;
    /** @type {{ key?: string, variantId?: string, unitEntries: number }[]} */
    const checkoutLines = [];

    for (const row of rows) {
      if (row.dataset.entriesMultiplier === 'true') {
        hasCartMultiplier = true;
        this.#paintLineLabel(row, 0, true);
        checkoutLines.push({
          key: row.dataset.key,
          variantId: row.dataset.variantId,
          unitEntries: 0,
        });
        continue;
      }

      const quantity = Number(row.dataset.lineQuantity);
      const perItem = this.#unitEntriesFor(row, perDollar);
      if (Number.isFinite(quantity) && quantity > 0) {
        baseTotal += perItem * quantity;
      }
      this.#paintLineLabel(row, perItem);
      checkoutLines.push({
        key: row.dataset.key,
        variantId: row.dataset.variantId,
        unitEntries: perItem,
      });
    }

    const total = hasCartMultiplier ? baseTotal * multiplierFactor : baseTotal;
    this.#paintTotal(total);
    this.#paintMultiplierPreview(baseTotal * multiplierFactor);
    void syncCartEntryProperties(checkoutLines);
    void syncCartTotalAttribute(total);
  }

  /**
   * @param {HTMLElement[]} rows
   */
  #paintPending(rows) {
    this.refs.count.textContent = '';
    for (const row of rows) {
      if (this.#isMerchRow(row)) this.#paintLineLabel(row, 0);
    }
  }

  /**
   * @param {number} total
   */
  #paintTotal(total) {
    this.refs.count.textContent = withCommas(Math.round(total));
  }

  /**
   * @param {number} previewTotal
   */
  #paintMultiplierPreview(previewTotal) {
    const roots = document.querySelectorAll('[data-multiplier-preview-count]');
    for (const el of roots) {
      if (!(el instanceof HTMLElement)) continue;
      el.textContent = withCommas(Math.round(previewTotal));
    }
  }

  /**
   * @param {HTMLElement} row
   * @param {number} perItem
   * @param {boolean} [forceHide]
   */
  #paintLineLabel(row, perItem, forceHide = false) {
    const label = row.querySelector('[data-entries-per-item]');
    if (!(label instanceof HTMLElement)) return;

    if (forceHide || !(perItem > 0)) {
      label.hidden = true;
      label.textContent = '';
      return;
    }

    const template = label.dataset.entriesLabelTemplate || 'Entries per item: %value%';
    label.textContent = template.replace('%value%', withCommas(Math.round(perItem)));
    label.hidden = false;
  }
}

if (!customElements.get('cart-total-entries-component')) {
  customElements.define('cart-total-entries-component', CartTotalEntries);
}
